import { Box, CircularProgress, MenuItem, Select, Tab, Tabs, Typography } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import ClinicBrandPayoutReports from 'src/clinic/views/clinicBrandPayoutReports/ClinicBrandPayoutReports'
import { EVENT_TYPE_CONSULTATION, EVENT_TYPE_THERAPY_SESSION } from 'src/consumer/constants'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { MentorClinicsContext } from 'src/mentor/layout'
import { getMPDataWithParams } from 'src/mentor/services'
import InvoiceListing from 'src/supportPortal/views/payoutReports/InvoiceListing'
import FeeModelsDisplay from '../ActiveFeeModels/components/FeeModelsDisplay'
import { AvailabilityPageContainer } from '../MentorAvailabilityPage/MentorAvailabilityPageStyles'
import MentorHeader from '../MentorHeader'

const TAB_BUSINESS_MODEL = 'business-model'
const TAB_PAYOUT_REPORTS = 'payout-reports'
const TAB_PAYOUT_INVOICES = 'payout-invoices'

// Per-brand, support-managed visibility (ClinicBrand.ownerPortalSettings). DEFAULT IS OFF:
// a surface shows only when its flag is explicitly true — absent settings mean hidden.
// The backend enforces the same flags on the underlying endpoints.
// (Google Business config now lives on the dedicated /mentor-home/integrations page.)
function getEnabledOwnerBusinessTabs(brand) {
  const settings = brand?.ownerPortalSettings
  return [
    {
      value: TAB_BUSINESS_MODEL,
      label: 'Business Model',
      enabled: settings?.showBusinessModels === true,
    },
    {
      value: TAB_PAYOUT_REPORTS,
      label: 'Payout Reports',
      enabled: settings?.showPayoutReports === true,
    },
    {
      value: TAB_PAYOUT_INVOICES,
      label: 'Payout Invoices',
      enabled: settings?.showPayoutInvoices === true,
    },
  ].filter((tab) => tab.enabled)
}

// Owner-facing entry point that consolidates the clinic brand's applicable
// business (engagement) models, payout reports and payout invoices — the same
// three surfaces support staff see per brand on the support clinics page.
export default function BusinessModelAndInvoicesPage() {
  const { associatedClinicBrands, loadingAssociationsData } = useContext(MentorClinicsContext)

  const [selectedBrandId, setSelectedBrandId] = useState()
  const [activeTab, setActiveTab] = useState(TAB_BUSINESS_MODEL)

  useEffect(() => {
    if (!selectedBrandId && associatedClinicBrands?.length > 0) {
      setSelectedBrandId(associatedClinicBrands?.[0]?.id)
    }
  }, [associatedClinicBrands, selectedBrandId])

  const selectedBrand = associatedClinicBrands?.find((brand) => brand?.id === selectedBrandId)
  const getBrandLabel = (brand) => brand?.brandName || brand?.title || brand?.id || ''

  const enabledTabs = getEnabledOwnerBusinessTabs(selectedBrand)
  // If support disabled the currently active surface (or the brand changed), fall back
  // to the first surface that is still enabled for this brand.
  const effectiveTab = enabledTabs.some((tab) => tab.value === activeTab)
    ? activeTab
    : enabledTabs[0]?.value

  return (
    <AvailabilityPageContainer>
      <MentorHeader showBackButton heading="Business Model & Invoices" />
      {loadingAssociationsData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !associatedClinicBrands?.length ? (
        <Typography sx={{ p: 3 }} color="text.secondary">
          No clinic brand is associated with your account yet.
        </Typography>
      ) : (
        <>
          {associatedClinicBrands?.length > 1 && (
            <Box sx={{ px: 2, pt: 2 }}>
              <Select
                size="small"
                value={selectedBrandId || ''}
                onChange={(e) => setSelectedBrandId(e.target.value)}
              >
                {associatedClinicBrands?.map((brand) => (
                  <MenuItem key={brand?.id} value={brand?.id}>
                    {getBrandLabel(brand)}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}
          {enabledTabs.length === 0 ? (
            <Typography sx={{ p: 3 }} color="text.secondary">
              Access to business model &amp; invoice details is currently disabled for this clinic
              brand. Please contact AUMHUM support if you think this is a mistake.
            </Typography>
          ) : (
            <Tabs
              sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
              value={effectiveTab}
              onChange={(e, val) => setActiveTab(val)}
              variant="scrollable"
              allowScrollButtonsMobile
            >
              {enabledTabs.map((tab) => (
                <Tab key={tab.value} label={tab.label} value={tab.value} />
              ))}
            </Tabs>
          )}
          {selectedBrandId && enabledTabs.length > 0 && (
            <>
              {effectiveTab === TAB_BUSINESS_MODEL && (
                <BrandBusinessModelTab clinicBrandId={selectedBrandId} />
              )}
              {effectiveTab === TAB_PAYOUT_REPORTS && (
                <BrandPayoutReportsTab clinicBrandId={selectedBrandId} />
              )}
              {effectiveTab === TAB_PAYOUT_INVOICES && (
                <BrandPayoutInvoicesTab
                  brand={{ id: selectedBrand?.id, title: getBrandLabel(selectedBrand) }}
                />
              )}
            </>
          )}
        </>
      )}
    </AvailabilityPageContainer>
  )
}

// Applicable business/engagement models for the brand, same renderer the
// support portal uses (FeeModelsDisplay) fed by the owner-scoped endpoints.
function BrandBusinessModelTab({ clinicBrandId }) {
  const [loading, setLoading] = useState(false)
  const [entityFeeModels, setEntityFeeModels] = useState()
  const [inApplicableEntityFeeModels, setInApplicableEntityFeeModels] = useState()
  const [feeModels, setFeeModels] = useState()
  const [feeModelGroups, setFeeModelGroups] = useState()

  useEffect(() => {
    let cancelled = false
    async function setup() {
      setLoading(true)
      const [modelsResp, groupsResp] = await Promise.all([
        getMPDataWithParams('/api/secure/clinic-admin/entity-fee-model/list', {
          clinicBrandId,
        }),
        getMPDataWithParams('/api/secure/clinic-admin/fee-model-group/list'),
      ])
      if (cancelled) {
        return
      }
      if (modelsResp?.status === 200 && isCommonResponseSuccessfulV2(modelsResp)) {
        const data = getDataMapFromCommonResponse(modelsResp)
        setEntityFeeModels(data?.['entity-fee-model'])
        setInApplicableEntityFeeModels(data?.['inApplicable__entity-fee-model'])
        setFeeModels(data?.['fee-model'])
      }
      if (groupsResp?.status === 200 && isCommonResponseSuccessfulV2(groupsResp)) {
        setFeeModelGroups(getDataMapFromCommonResponse(groupsResp)?.['fee-model-group'])
      }
      setLoading(false)
    }
    if (clinicBrandId) {
      setup()
    }
    return () => {
      cancelled = true
    }
  }, [clinicBrandId])

  return (
    <FeeModelsDisplay
      entityFeeModels={entityFeeModels}
      inApplicableEntityFeeModels={inApplicableEntityFeeModels}
      feeModels={feeModels}
      feeModelGroups={feeModelGroups}
      forMentor={false}
      loading={loading}
    />
  )
}

function BrandPayoutReportsTab({ clinicBrandId }) {
  const [eventType, setEventType] = useState(EVENT_TYPE_CONSULTATION)

  return (
    <>
      <Tabs sx={{ px: 2 }} value={eventType} onChange={(e, val) => setEventType(val)}>
        <Tab label="Consultation" value={EVENT_TYPE_CONSULTATION} />
        <Tab label="Therapy" value={EVENT_TYPE_THERAPY_SESSION} />
      </Tabs>
      <ClinicBrandPayoutReports
        key={`${clinicBrandId}-${eventType}`}
        apiUrl="/api/secure/clinic-admin/reports/payout-reports-for-doctor-admin/paginated"
        isForClinicBrand={true}
        eventType={eventType}
        clinicBrandId={clinicBrandId}
        showSummaryReport
      />
    </>
  )
}

function BrandPayoutInvoicesTab({ brand }) {
  function prepareBookedEventsDataFromApiResponse(response) {
    const data = getDataMapFromCommonResponse(response)
    return data?.['booked-event-info']
  }

  return (
    <InvoiceListing
      key={brand?.id}
      entityListOverride={[brand]}
      hideActions
      hideTargetEntityIdFilter
      targetEntityType="clinic-brand"
      invoicesListingApiSignature="/api/secure/clinic-admin/payout-invoice/paginated"
      bookedEventsListingApiSignature="/api/secure/clinic-admin/payout-invoice/booked-events"
      prepareBookedEventsDataFromApiResponse={prepareBookedEventsDataFromApiResponse}
      initialFilters={{
        targetEntityIds: brand?.id,
        targetEntityTypes: 'clinic-brand',
      }}
    />
  )
}
